import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Map, TreePine, Waves, Ruler, Hammer, ChevronDown, Play, X } from "lucide-react";
import { api, assetUrl } from "../lib/api";
import { useReveal } from "../lib/useReveal";
import HotspotPlan from "../components/site/HotspotPlan";
import AreaMapView from "../components/site/AreaMapView";
import PhotoMarquee from "../components/site/PhotoMarquee";
import Reveal from "../components/site/Reveal";
import CountUp from "../components/site/CountUp";
import SectionWave from "../components/site/SectionWave";
import Magnetic from "../components/site/Magnetic";
import { gsap, prefersReducedMotion } from "../lib/motion";
import LotDetailDialog from "../components/site/LotDetailDialog";
import EnquiryDialog from "../components/site/EnquiryDialog";
import { STATUS_COLORS, STATUS_LABELS } from "../lib/constants";

const DEER_PARK_REGION = { x: 0.135, y: 0.17, w: 0.15, h: 0.17, label: "Deer Park Estates" };

const KPI = ({ value, label }) => (
  <div className="border-l border-[#F3EEE3]/15 pl-4">
    <p className="font-display text-3xl md:text-4xl text-[#F3EEE3]"><CountUp value={value} /></p>
    <p className="eyebrow text-[0.55rem] text-[#C9B79C] mt-1">{label}</p>
  </div>
);

const stageTone = (s) => {
  if (s.status === "available") return { dot: "#C9B79C", ring: "ring-[#C9B79C]", txt: "text-[#12201A]", bg: "bg-[#C9B79C]" };
  if (s.status === "completed") return { dot: "#3E7A5E", ring: "ring-[#3E7A5E]/40", txt: "text-[#F3EEE3]", bg: "bg-[#26402F]" };
  return { dot: "#5B6B7A", ring: "ring-[#5B6B7A]/30", txt: "text-[#F3EEE3]/70", bg: "bg-[#1B2E24]" };
};

const Home = () => {
  const [project, setProject] = useState({});
  const [stats, setStats] = useState({});
  const [stages, setStages] = useState([]);
  const [builders, setBuilders] = useState([]);
  const [commercial, setCommercial] = useState({});
  const [apartment, setApartment] = useState({});
  const [videos, setVideos] = useState([]);
  const [videoActive, setVideoActive] = useState(null);
  const [activeAvail, setActiveAvail] = useState(null);
  const [activeParcels, setActiveParcels] = useState([]);
  const [activeDetail, setActiveDetail] = useState(null);
  const [activeEnq, setActiveEnq] = useState(null);
  const heroContentRef = useRef(null);
  const heroImgRef = useRef(null);
  const railSectionRef = useRef(null);
  const railViewportRef = useRef(null);
  const railTrackRef = useRef(null);

  useEffect(() => {
    api.get("/project").then((r) => setProject(r.data)).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/stages").then((r) => setStages(r.data)).catch(() => {});
    api.get("/builders").then((r) => setBuilders(r.data)).catch(() => {});
    api.get("/sections/commercial").then((r) => setCommercial(r.data || {})).catch(() => {});
    api.get("/sections/apartment").then((r) => setApartment(r.data || {})).catch(() => {});
    api.get("/videos").then((r) => setVideos(r.data || [])).catch(() => {});
  }, []);

  useReveal([stages.length, builders.length, videos.length, project.area_map_image]);
  const active = stages.find((s) => s.status === "available");

  // Hero: content drifts up & fades, image pushes in — a scrubbed parallax tied to scroll.
  useLayoutEffect(() => {
    if (prefersReducedMotion()) return;
    const ctx = gsap.context(() => {
      if (heroContentRef.current) {
        gsap.to(heroContentRef.current, {
          yPercent: -14,
          opacity: 0,
          ease: "none",
          scrollTrigger: { trigger: heroContentRef.current, start: "top top", end: "bottom top", scrub: true },
        });
      }
      if (heroImgRef.current) {
        gsap.fromTo(
          heroImgRef.current,
          { scale: 1.12, yPercent: 0 },
          {
            scale: 1.24,
            yPercent: 10,
            ease: "none",
            scrollTrigger: { trigger: heroImgRef.current.parentElement, start: "top top", end: "bottom top", scrub: true },
          }
        );
      }
    });
    return () => ctx.revert();
  }, []);

  // Stages rail: on desktop the row drifts horizontally as the section scrolls
  // through the viewport (no pinning → no layout jumps); mobile keeps native scroll.
  useLayoutEffect(() => {
    if (prefersReducedMotion() || !stages.length) return;
    const mm = gsap.matchMedia();
    mm.add("(min-width: 1024px)", () => {
      const track = railTrackRef.current;
      const viewport = railViewportRef.current;
      const section = railSectionRef.current;
      if (!track || !viewport || !section) return;
      const overflow = () => Math.max(0, track.scrollWidth - viewport.clientWidth);
      if (overflow() <= 0) return;
      const tween = gsap.fromTo(
        track,
        { x: 0 },
        {
          x: () => -overflow(),
          ease: "none",
          scrollTrigger: {
            trigger: section,
            start: "top 72%",
            end: "bottom top",
            scrub: 1,
            invalidateOnRefresh: true,
          },
        }
      );
      return () => {
        if (tween.scrollTrigger) tween.scrollTrigger.kill();
        tween.kill();
        gsap.set(track, { x: 0 });
      };
    });
    return () => mm.revert();
  }, [stages.length]);

  useEffect(() => {
    if (!active) return;
    api.get(`/parcels?stage_id=${active.id}`)
      .then((r) => { setActiveAvail(r.data.filter((p) => p.status === "available").length); setActiveParcels(r.data); })
      .catch(() => {});
  }, [active?.id]);

  const activePins = activeParcels.filter((p) => p.hotspot && typeof p.hotspot.x === "number").map((p) => ({
    id: p.id, x: p.hotspot.x, y: p.hotspot.y, label: String(p.lot_number ?? p.parcel_no),
    color: STATUS_COLORS[p.status], title: `Lot ${p.lot_number ?? p.parcel_no}`, parcel: p,
  }));

  return (
    <div className="bg-[#12201A]">
      {/* ------------------------------------------------------------ hero */}
      <section className="relative h-[100svh] min-h-[640px] w-full overflow-hidden grain">
        <video
          ref={heroImgRef}
          className="absolute inset-0 w-full h-full object-cover md:hidden"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster="/deerpark_hero.jpg"
          data-testid="hero-video-mobile"
        >
          <source src="/hero/deerpark_hero.mp4" type="video/mp4" />
        </video>
        <video
          className="hidden md:block absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster="/hero/deerpark_hero_poster.jpg"
          data-testid="hero-video"
        >
          <source src="/hero/deerpark_hero.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0C1510]/60 via-[#0C1510]/20 to-[#0C1510]/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0C1510]/70 via-[#0C1510]/10 to-transparent" />
        <div ref={heroContentRef} className="relative z-10 h-full max-w-[1500px] mx-auto px-5 md:px-8 flex flex-col justify-center">
          <p className="eyebrow text-[#C9B79C] mb-5 reveal">{project.location || "Spruce Grove · Alberta"}</p>
          <h1 className="font-display font-light text-[#F3EEE3] leading-[1.0] tracking-tight text-[2.8rem] sm:text-6xl md:text-[5.2rem] max-w-4xl reveal" style={{ transitionDelay: "80ms" }}>
            {project.tagline || "A community rooted in nature, built for life."}
          </h1>
          <p className="mt-7 max-w-xl text-[#F3EEE3]/80 text-base md:text-lg font-light leading-relaxed reveal" style={{ transitionDelay: "160ms" }}>
            {project.subtitle}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 reveal" style={{ transitionDelay: "240ms" }}>
            <Magnetic strength={0.35}>
              <Link to="/master-plan" data-testid="cta-masterplan" className="group inline-flex items-center justify-center gap-2.5 bg-[#F3EEE3] text-[#12201A] px-8 py-4 rounded-full font-medium hover:bg-white transition-colors">
                <Map size={17} /> Explore the Master Plan <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </Magnetic>
          </div>
          <p className="mt-6 reveal" style={{ transitionDelay: "300ms" }} data-testid="hero-homebuyer-note">
            <Link to="/builders" className="group inline-flex items-center gap-3 text-[#F3EEE3] text-2xl md:text-4xl font-display">
              <span className="font-semibold">Looking for a home? <span className="text-[#C9B79C] link-underline font-normal">Meet the builders</span></span>
              <ArrowRight size={30} className="text-[#C9B79C] group-hover:translate-x-1.5 transition-transform shrink-0" />
            </Link>
          </p>
        </div>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1.5 text-[#F3EEE3]/55">
          <span className="eyebrow text-[0.5rem]">Scroll to discover</span>
          <ChevronDown size={16} className="animate-bounce" />
        </div>
        {project.developer && (
          <div className="absolute bottom-5 right-5 z-10 text-[0.58rem] tracking-widest uppercase text-[#F3EEE3]/45 hidden sm:block">
            Developed by {project.developer}
          </div>
        )}
      </section>

      {/* --------------------------------------------- now-selling banner */}
      {active && (
        <section className="relative bg-[#C9B79C]" data-testid="home-nowselling-banner">
          <div className="max-w-[1500px] mx-auto px-5 md:px-8 py-3.5 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3 text-[#12201A]">
              <span className="relative flex h-3 w-3 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#12201A]/40" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-[#12201A]" />
              </span>
              <p className="text-sm md:text-base font-medium text-center sm:text-left">
                <span className="uppercase tracking-[0.14em] text-[0.7rem] font-semibold">Now Selling</span>
                <span className="mx-2 text-[#12201A]/40">·</span>
                {active.name}
                {activeAvail != null && (
                  <>
                    <span className="mx-2 text-[#12201A]/40">·</span>
                    <span className="font-display text-lg">{activeAvail}</span> lots available now
                  </>
                )}
              </p>
            </div>
            <Link
              to={`/stages/${active.id}`}
              data-testid="home-nowselling-cta"
              className="cta-shine cta-pulse group inline-flex items-center gap-2 bg-[#12201A] text-[#F3EEE3] px-6 py-2.5 rounded-full text-sm font-medium hover:bg-[#0C1510] transition-colors whitespace-nowrap"
            >
              <span className="inline-flex items-center gap-2">View available lots <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" /></span>
            </Link>
          </div>
          <SectionWave position="bottom" color="#C9B79C" heightClass="h-[16px] md:h-[26px]" data-testid="nowselling-wave" />
        </section>
      )}

      {/* ------------------------------------------- interactive active-stage map */}
      {active && activePins.length > 0 && (
        <section className="max-w-[1500px] mx-auto px-5 md:px-8 py-20" data-testid="home-active-map">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
            <div>
              <p className="eyebrow text-[#C9B79C] mb-3 flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C9B79C]/50" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#C9B79C]" />
                </span>
                Now Selling · {active.name}
              </p>
              <Reveal as="h2" className="font-display font-light text-4xl md:text-5xl text-[#F3EEE3]">Explore lots on the interactive plan</Reveal>
              <p className="text-[#F3EEE3]/60 mt-3 max-w-xl">Tap any numbered lot to see its size, frontage, features and availability — then enquire in one click.</p>
            </div>
            <div className="flex items-center gap-4 text-xs text-[#F3EEE3]/60 shrink-0">
              <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLORS.available }} /> Available</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLORS.reserved }} /> Reserved</span>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden border border-[#F3EEE3]/12 bg-[#0F1B15] p-3 md:p-4">
            <HotspotPlan src={active.plan_image} pins={activePins} onPinClick={(pin) => setActiveDetail(pin.parcel)} fit />
          </div>
          <div className="mt-6 flex justify-center">
            <Link to={`/stages/${active.id}`} data-testid="home-active-map-cta" className="group inline-flex items-center gap-2 border border-[#F3EEE3]/25 text-[#F3EEE3] px-6 py-3 rounded-full text-sm hover:border-[#C9B79C]/60 transition-colors">
              Open full {active.name} plan, filters &amp; documents <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </section>
      )}

      {/* ---------------------------------------- running photo marquee */}
      <PhotoMarquee />

      {/* ------------------------------------------------------- KPI strip */}
      <section className="border-t border-[#ffffff14] bg-[#0F1B15]">
        <div className="max-w-[1500px] mx-auto px-5 md:px-8 pt-12 pb-24 grid grid-cols-2 md:grid-cols-5 gap-8">
          <KPI value={project.total_area || "—"} label="Total Area" />
          <KPI value={stats.stages ?? "—"} label="Development Stages" />
          <KPI value={stats.available ?? "—"} label="Lots Selling Now" />
          <KPI value={stats.sold_percent != null ? `${stats.sold_percent}%` : "—"} label="Lots Committed" />
          <KPI value={builders.length || project.builders_count || "—"} label="Custom Builders" />
        </div>
      </section>

      {/* ------------------------------------------------------ nature/scale */}
      <section className="relative bg-[#F3EEE3]">
        <SectionWave position="top" color="#F3EEE3" data-testid="nature-wave-top" />
        <div className="max-w-[1500px] mx-auto px-5 md:px-8 py-24">
          <p className="eyebrow text-[#8F7539] mb-4 reveal">Nature &amp; Scale</p>
          <Reveal as="h2" className="font-display font-light text-3xl md:text-5xl text-[#12201A] max-w-3xl leading-tight">
            220+ acres of tree-lined streets, ponds and preserved nature — northwest of Spruce Grove.
          </Reveal>
          <div className="grid md:grid-cols-4 gap-6 mt-14">
            {[
              { icon: TreePine, t: "Preserved Nature", d: "Natural areas, tree-lined streets and open spaces woven through the community." },
              { icon: Waves, t: "Two Ponds", d: "Stormwater ponds and green corridors at the heart of the plan." },
              { icon: Ruler, t: "50 & 60 ft Lots", d: "60-foot lots with triple garages and new 50-foot lots with double garages." },
              { icon: Hammer, t: "Custom Builders", d: `${builders.length || "Trusted"} custom home builders crafting homes at Deer Park.` },
            ].map((c, i) => (
              <div key={c.t} className="rounded-2xl border border-[#12201A]/12 bg-white p-7 reveal shadow-[0_2px_14px_rgba(18,32,26,0.06)]" style={{ transitionDelay: `${i * 70}ms` }}>
                <c.icon size={22} className="text-[#8F7539]" />
                <p className="font-display text-xl text-[#12201A] mt-4">{c.t}</p>
                <p className="text-[#12201A]/65 text-sm mt-2 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
        {/* organic bottom edge (light drips into the dark section below) */}
        <SectionWave position="bottom" color="#F3EEE3" data-testid="nature-wave-bottom" />
      </section>

      {/* -------------------------------------------- explore spruce grove */}
      {project.area_map_image && (
        <section className="max-w-[1500px] mx-auto px-5 md:px-8 py-24">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 reveal">
            <div>
              <p className="eyebrow text-[#C9B79C] mb-3">Explore Spruce Grove</p>
              <Reveal as="h3" className="font-display font-light text-3xl md:text-5xl text-[#F3EEE3] max-w-2xl leading-tight">Deer Park's place in the city.</Reveal>
            </div>
            <Link to="/location" className="link-underline hidden sm:inline-flex items-center gap-2 text-[#C9B79C] text-sm whitespace-nowrap">Parks, schools &amp; amenities <ArrowRight size={15} /></Link>
          </div>
          <div className="mt-8 reveal">
            <AreaMapView mapSrc={project.area_map_image} infoSrc={project.area_info_image} />
          </div>
        </section>
      )}

      {/* ---------------------------------------------- stages progress rail */}
      <section ref={railSectionRef} className="relative border-y border-[#ffffff14] bg-[#0F1B15]">
        <div className="max-w-[1500px] mx-auto px-5 md:px-8 py-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 reveal">
            <div>
              <p className="eyebrow text-[#C9B79C] mb-3">Development Progress</p>
              <Reveal as="h3" className="font-display font-light text-3xl md:text-4xl text-[#F3EEE3]">Twelve stages, one growing community.</Reveal>
            </div>
            {active && (
              <Link to={`/stages/${active.id}`} className="link-underline inline-flex items-center gap-2 text-[#C9B79C] text-sm">
                {active.name} is now selling <ArrowRight size={15} />
              </Link>
            )}
          </div>

          <div className="lg:hidden mt-8 mb-1 flex items-center gap-2 text-[#C9B79C] text-xs" data-testid="stages-swipe-hint">
            <span>Swipe to see all {stages.length} stages</span>
            <ArrowRight size={14} className="swipe-nudge" />
          </div>

          <div className="relative">
            <div ref={railViewportRef} className="mt-4 lg:mt-12 overflow-x-auto lg:overflow-hidden hide-scrollbar">
              <div ref={railTrackRef} className="flex gap-2.5 pb-3 w-max">
                {stages.map((s) => {
                  const t = stageTone(s);
                  const isActive = s.status === "available";
                  return (
                    <Link
                      key={s.id}
                      to={`/stages/${s.id}`}
                      data-testid={`home-stage-${s.number}`}
                      className={`group shrink-0 w-[190px] md:w-[220px] rounded-2xl p-6 ${t.bg} ring-1 ${t.ring} transition-transform hover:-translate-y-1 ${isActive ? "shadow-[0_0_0_2px_#C9B79C] scale-[1.03]" : ""}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`font-display text-4xl ${isActive ? "text-[#12201A]" : "text-[#F3EEE3]/35"}`}>{String(s.number).padStart(2, "0")}</span>
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: t.dot }} />
                      </div>
                      <p className={`mt-8 text-base font-medium ${t.txt}`}>{s.name}</p>
                      <p className={`text-[0.72rem] mt-1 ${isActive ? "text-[#12201A]/70" : "text-[#F3EEE3]/45"}`}>{s.status_label}</p>
                    </Link>
                  );
                })}
              </div>
            </div>
            <div className="lg:hidden pointer-events-none absolute inset-y-0 right-0 w-14 bg-gradient-to-l from-[#0F1B15] to-transparent" />
          </div>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[0.7rem] text-[#F3EEE3]/55">
            <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#C9B79C]" /> Now selling</span>
            <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#3E7A5E]" /> Built out / Committed</span>
            <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#5B6B7A]" /> In development</span>
          </div>
        </div>
      </section>

      {/* ----------------------------------------------------- videos row */}
      {videos.length > 0 && (
        <section className="bg-[#0F1B15] border-y border-[#ffffff10]">
          <div className="max-w-[1500px] mx-auto px-5 md:px-8 py-20 reveal">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-8">
              <div>
                <p className="eyebrow text-[#C9B79C] mb-3">Watch</p>
                <Reveal as="h3" className="font-display text-3xl md:text-4xl text-[#F3EEE3] leading-tight">See Deer Park from above.</Reveal>
                <p className="text-[#F3EEE3]/70 mt-3 max-w-xl leading-relaxed">Aerial tours of the ponds, parks and homes — straight from our channel.</p>
              </div>
              <Link to="/videos" data-testid="home-videos-all" className="group inline-flex items-center gap-2 border border-[#C9B79C]/45 text-[#F3EEE3] px-7 py-3.5 rounded-full font-medium hover:bg-[#C9B79C] hover:text-[#12201A] transition-colors whitespace-nowrap shrink-0">
                Watch all videos <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {videos.slice(0, 2).map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setVideoActive(v)}
                  data-testid={`home-video-${v.id}`}
                  className="group text-left"
                >
                  <div className="relative aspect-video rounded-2xl overflow-hidden border border-[#F3EEE3]/12 bg-[#0C1510]">
                    <img src={v.thumbnail} alt={v.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-[#0C1510]/30 group-hover:bg-[#0C1510]/15 transition-colors" />
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="w-16 h-16 rounded-full bg-[#F3EEE3]/95 text-[#12201A] flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                        <Play size={26} className="ml-1 fill-current" />
                      </span>
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ----------------------------------------------------- master plan */}
      <section className="relative overflow-hidden">
        <img src="/deerpark_hero.jpg" alt="Aerial view of Deer Park Estates" data-parallax="0.12" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-[#0C1510]/80" />
        <div className="relative z-10 max-w-[1500px] mx-auto px-5 md:px-8 py-24 flex flex-col md:flex-row items-center justify-between gap-8 reveal">
          <div className="max-w-xl">
            <p className="eyebrow text-[#C9B79C] mb-3">Interactive</p>
            <Reveal as="h3" className="font-display text-3xl md:text-5xl text-[#F3EEE3] leading-tight">Walk the whole territory from above.</Reveal>
            <p className="text-[#F3EEE3]/75 mt-5 leading-relaxed">
              Open the interactive master plan to explore every stage and parcel — availability, land use and how the
              community fits together — on a live satellite map.
            </p>
          </div>
          <Magnetic strength={0.4}>
            <Link to="/master-plan" className="group inline-flex items-center gap-2 bg-[#C9B79C] text-[#12201A] px-8 py-4 rounded-full font-medium hover:bg-[#d8c8ac] transition-colors whitespace-nowrap">
              Open Master Plan <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Magnetic>
        </div>
      </section>

      {/* -------------------------------------------------- featured builders */}
      <section className="max-w-[1500px] mx-auto px-5 md:px-8 py-24">
        <div className="flex items-end justify-between gap-4 reveal">
          <div>
            <p className="eyebrow text-[#C9B79C] mb-3">Our Builders</p>
            <Reveal as="h3" className="font-display font-light text-3xl md:text-5xl text-[#F3EEE3]">Homes crafted by trusted custom builders.</Reveal>
          </div>
          <Link to="/builders" className="link-underline hidden sm:inline-flex items-center gap-2 text-[#C9B79C] text-sm whitespace-nowrap">All builders <ArrowRight size={15} /></Link>
        </div>
        <div className="sm:hidden mt-8 mb-1 flex items-center gap-2 text-[#C9B79C] text-xs" data-testid="builders-swipe-hint">
          <span>Swipe to see all builders</span>
          <ArrowRight size={14} className="swipe-nudge" />
        </div>
        <div className="relative">
          <div className="flex gap-4 md:gap-6 mt-4 sm:mt-10 overflow-x-auto hide-scrollbar pb-2">
            {builders.map((b, i) => {
              const cover = b.gallery?.[0] || b.logo_url;
              return (
                <Link key={b.id} to={`/builders/${b.id}`} data-testid={`home-builder-${i}`} className="group shrink-0 w-[78%] sm:w-[46%] md:w-[360px] rounded-2xl overflow-hidden border border-[#F3EEE3]/12 bg-[#0F1B15] reveal hover:border-[#C9B79C]/40 transition-colors" style={{ transitionDelay: `${i * 80}ms` }}>
                  <div className="aspect-[4/3] overflow-hidden bg-[#0C1510]">
                    {cover ? (
                      <img src={assetUrl(cover)} alt={b.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center font-display text-2xl text-[#F3EEE3]/40">{b.name}</div>
                    )}
                  </div>
                  <div className="p-6">
                    <p className="font-display text-xl text-[#F3EEE3]">{b.name}</p>
                    <p className="text-[#F3EEE3]/60 text-sm mt-2 leading-relaxed line-clamp-2">{b.tagline || b.description}</p>
                    <span className="inline-flex items-center gap-1.5 text-[#C9B79C] text-xs mt-4 group-hover:gap-2.5 transition-all">View builder <ArrowRight size={13} /></span>
                  </div>
                </Link>
              );
            })}
          </div>
          <div className="pointer-events-none absolute inset-y-0 right-0 w-14 md:w-20 bg-gradient-to-l from-[#12201A] to-transparent" />
        </div>
      </section>

      {/* ------------------------------------------- commercial + apartment */}
      <section className="border-t border-[#ffffff14] bg-[#0F1B15]">
        <div className="max-w-[1500px] mx-auto px-5 md:px-8 py-24 grid md:grid-cols-2 gap-6">
          {[
            { s: commercial, to: "/commercial", fallbackT: "Commercial Centre", img: commercial.gallery?.[0] },
            { s: apartment, to: "/apartment", fallbackT: "High-Density Apartment", img: apartment.gallery?.[0] },
          ].map((c, i) => (
            <Link key={i} to={c.to} className="group relative rounded-2xl overflow-hidden min-h-[320px] flex items-end reveal border border-[#F3EEE3]/12" style={{ transitionDelay: `${i * 90}ms` }}>
              {c.img ? (
                <img src={assetUrl(c.img)} alt={c.s.title || c.fallbackT} className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-70 group-hover:scale-105 transition-all duration-700" />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-[#1B2E24] to-[#0C1510]" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0C1510] via-[#0C1510]/30 to-transparent" />
              <div className="relative z-10 p-8">
                {c.s.status_label && (
                  <span className="inline-flex items-center gap-2 rounded-full bg-[#C9B79C]/20 border border-[#C9B79C]/40 px-3 py-1 text-[0.66rem] tracking-wide text-[#F3EEE3] mb-4">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9B79C]" /> {c.s.status_label}
                  </span>
                )}
                <h3 className="font-display text-3xl text-[#F3EEE3]">{c.s.title || c.fallbackT}</h3>
                <p className="text-[#F3EEE3]/70 text-sm mt-2 max-w-md line-clamp-2">{c.s.intro}</p>
                <span className="inline-flex items-center gap-1.5 text-[#C9B79C] text-xs mt-4 group-hover:gap-2.5 transition-all">Learn more <ArrowRight size={13} /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ------------------------------------------------- conversion close */}
      <section className="max-w-[1500px] mx-auto px-5 md:px-8 py-24">
        <div className="rounded-3xl border border-[#C9B79C]/25 bg-[#0F1B15] p-10 md:p-16 text-center reveal">
          <Reveal as="h3" className="font-display font-light text-3xl md:text-5xl text-[#F3EEE3] max-w-3xl mx-auto leading-tight">
            Ready to build your home at Deer Park?
          </Reveal>
          <p className="text-[#F3EEE3]/65 mt-5 max-w-xl mx-auto">
            Ask about available lots in Stage 8, meet the builders, or book a visit to the community.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-[#F3EEE3] text-[#12201A] px-8 py-4 rounded-full font-medium hover:bg-white transition-colors">
              Ask about a lot <ArrowRight size={16} />
            </Link>
            <Link to="/stages" className="inline-flex items-center justify-center gap-2 border border-[#F3EEE3]/40 text-[#F3EEE3] px-8 py-4 rounded-full font-medium hover:bg-[#F3EEE3]/10 transition-colors">
              Browse the stages
            </Link>
          </div>
        </div>
      </section>

      <LotDetailDialog
        open={!!activeDetail}
        parcel={activeDetail}
        builder={builders.find((b) => b.id === activeDetail?.builder_id)}
        onClose={() => setActiveDetail(null)}
        onEnquire={(p) => { setActiveDetail(null); setActiveEnq(p); }}
      />
      <EnquiryDialog
        open={!!activeEnq}
        onClose={() => setActiveEnq(null)}
        context={activeEnq ? { parcel_id: activeEnq.id, stage_id: activeEnq.stage_id, label: `Lot ${activeEnq.lot_number ?? activeEnq.parcel_no}`, source: "home-active-map" } : {}}
      />

      {videoActive && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="home-video-modal">
          <div className="absolute inset-0 bg-[#0C1510]/90 backdrop-blur-sm" onClick={() => setVideoActive(null)} />
          <div className="relative w-full max-w-4xl">
            <button onClick={() => setVideoActive(null)} data-testid="home-video-modal-close" className="absolute -top-11 right-0 text-[#F3EEE3]/70 hover:text-[#F3EEE3] inline-flex items-center gap-1.5 text-sm">
              Close <X size={18} />
            </button>
            <div className="aspect-video rounded-2xl overflow-hidden border border-[#F3EEE3]/15 bg-black">
              <iframe
                className="w-full h-full"
                src={`https://www.youtube-nocookie.com/embed/${videoActive.id}?autoplay=1&rel=0`}
                title={videoActive.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <p className="mt-3 text-[#F3EEE3] font-medium">{videoActive.title}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
